import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Pencil, Layers, UserCheck } from "lucide-react";
import { FaTrash } from "react-icons/fa";
import ConfirmDialogBox from "../../../confirmdiolog/ConfirmDiologBox";


const topics = [
  { value: "1", label: "React Basics" },
  { value: "2", label: "React Hooks" },
  { value: "3", label: "State Management" },
];

/*Duration */
const durations = Array.from({ length: 12 }, (_, i) => ({
  value: (i + 1) * 5,
  label: `${(i + 1) * 5} mins`,
}));

const Assessment = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [assessmentName, setAssessmentName] = useState("");
  const [topicId, setTopicId] = useState("");
  const [questionsToBePresented, setQuestionsToBePresented] = useState("");
  const [duration, setDuration] = useState("");
  const [reviewable, setReviewable] = useState(true);
  const [errors, setErrors] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  /* 🔹 Validation (ONLY Topic mandatory) */
  const validate = () => {
    const e = {};
    if (!topicId) e.topicId = "Please select a topic";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* 🔹 Submit */
  const onSubmitHandler = (e) => {
    e.preventDefault();
    if (!validate()) return;

    console.log("ASSESSMENT DATA", {
      topicId,
      assessmentName,
      questionsToBePresented,
      duration,
      reviewable,
    });

    alert("Dummy submit – check console");
    navigate("/masters-assessments");
  };

  return (
    <div className="w-full mt-4 text-gray-800 dark:text-gray-100">
      {isEdit && (
        <div className="flex justify-end gap-8 mb-6 text-base">
          <button
            onClick={() => navigate(`/masters-assessments/${id}/mcq`)}
            className="flex items-center gap-2 underline"
          >
            <Pencil size={16} /> Edit MCQ
          </button>

          <button
            onClick={() => navigate(`/masters-assessments/${id}/sessions`)}
            className="flex items-center gap-2 underline"
          >
            <Layers size={16} /> Sessions
          </button>

          <button
            onClick={() => navigate(`/masters-assessments/${id}/attendees`)}
            className="flex items-center gap-2 underline"
          >
            <UserCheck size={16} /> Attendees
          </button>
        </div>
      )}

      <form onSubmit={onSubmitHandler} className="space-y-6 w-full">

        {/* Topic (ONLY REQUIRED) */}
        <div className="grid grid-cols-12 gap-4 items-center">
          <label className="col-span-2 text-base">
            Topic <span className="text-red-500">*</span>
          </label>
          <div className="col-span-10">
            <select
              value={topicId}
              onChange={(e) => {
                setTopicId(e.target.value);
                setErrors({});
              }}
              className="w-full h-12 rounded px-3 border bg-white dark:bg-slate-800"
            >
              <option value="">Select Topic</option>
              {topics.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            {errors.topicId && (
              <p className="text-red-500 text-base">{errors.topicId}</p>
            )}
          </div>
        </div>

        {/* Assessment Name (Optional) */}
        <div className="grid grid-cols-12 gap-4 items-center">
          <label className="col-span-2 text-base">Assessment Name</label>
          <div className="col-span-10">
            <input
              value={assessmentName}
              onChange={(e) => setAssessmentName(e.target.value)}
              className="w-full h-12 rounded px-3 border bg-white dark:bg-slate-800"
            />
          </div>
        </div>

        {/* Questions (Optional) */}
        <div className="grid grid-cols-12 gap-4 items-center">
          <label className="col-span-2 text-base">
            Questions to be Presented
          </label>
          <div className="col-span-10">
            <input
              type="number"
              value={questionsToBePresented}
              onChange={(e) => setQuestionsToBePresented(e.target.value)}
              className="w-full h-12 rounded px-3 border bg-white dark:bg-slate-800"
            />
          </div>
        </div>

        {/* Duration (Optional) */}
        <div className="grid grid-cols-12 gap-4 items-center">
          <label className="col-span-2 text-base">Duration</label>
          <div className="col-span-10">
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full h-12 rounded px-3 border bg-white dark:bg-slate-800"
            >
              <option value="">Select Duration</option>
              {durations.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Review */}
        <div className="grid grid-cols-12 gap-4">
          <label className="col-span-2"></label>
          <div className="col-span-10">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={reviewable}
                onChange={(e) => setReviewable(e.target.checked)}
              />
              Review Available
            </label>
          </div>
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-12 mt-10">
          <div className="col-span-10 col-start-3 flex gap-4">

            {isEdit && (
              <button
                type="button"
                onClick={() => setShowDeleteDialog(true)}
                className="px-6 py-2 rounded flex items-center gap-2 border border-red-500 text-red-500"
              >
                <FaTrash /> Delete
              </button>
            )}

            <div className="ml-auto flex gap-4">
              <button
                type="button"
                onClick={() => navigate("/masters-assessments")}
                className="px-6 py-2 rounded border"
              >
                Cancel
              </button>

              <button
                type="submit"
                className="px-6 py-2 rounded bg-[#0D3744] text-white"
              >
                {isEdit ? "Update" : "Submit"}
              </button>
            </div>

          </div>
        </div>
      </form>

      <ConfirmDialogBox
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onConfirm={() => {
          setShowDeleteDialog(false);
          alert("Dummy delete");
          navigate("/masters-assessments");
        }}
        title="Are you sure you want to delete this assessment?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default Assessment;